import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiShield } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto px-6 py-12"
    >
      <Link to="/" className="inline-flex items-center gap-2 text-emerald-600 font-bold mb-8 hover:gap-3 transition-all">
        <SafeIcon icon={FiArrowLeft} /> Back to Home
      </Link>

      <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl border border-slate-100">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
            <SafeIcon icon={FiShield} className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900">Privacy Policy</h1>
            <p className="text-sm text-slate-400 font-medium">Last Updated: February 10, 2026</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-600 font-medium leading-relaxed">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-2xl text-slate-800">
            <strong className="text-blue-900 block mb-2">🔒 Your Privacy, Simplified:</strong>
            <strong>No data are collected via the web app.</strong><br />
            ConnectGo is designed with your privacy as a priority. We don't collect, store, or share any personal information about you. You can play freely, knowing your privacy is fully protected.
          </div>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">1. Our Privacy Commitment</h2>
            <p>At ConnectGo, we believe your privacy matters. This Privacy Policy explains our approach in clear, straightforward terms so you understand exactly how we handle (or more accurately, don't handle) your information.</p>
            <p className="mt-2">This policy applies to all users of ConnectGo, accessible at <a href="https://ConnectGo.au/" className="text-indigo-600 underline">ConnectGo.au/</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">2. What Information We Collect</h2>
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl text-slate-800 mb-4">
              <strong className="text-emerald-900 block mb-1">✓ The Simple Answer: None.</strong>
              No data are collected via the web app. ConnectGo does not collect, process, store, or transmit any personal information about you.
            </div>
            <p>This means:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>No personal details:</strong> We don't ask for or collect your name, email, age, school, or any identifying information</li>
              <li><strong>No gameplay data:</strong> We don't track your scores, progress, or how you play the game</li>
              <li><strong>No technical tracking:</strong> We don't use cookies, analytics, or tracking technologies to monitor your activity</li>
              <li><strong>No account required:</strong> There's no login system, so there's nothing to register or any credentials to store</li>
              <li><strong>No third-party sharing:</strong> Since we don't collect data, there's nothing to share with anyone</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">3. How Your Browser May Store Information Locally</h2>
            <p>While ConnectGo itself doesn't collect data, your web browser may temporarily store some information on your own device to make the game work properly. This is standard browser behaviour and happens entirely on your device:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Local browser storage:</strong> Your browser might save your current game session so it remembers your progress if you refresh the page</li>
              <li><strong>Browser cache:</strong> Your browser may temporarily store game files to load the game faster on future visits</li>
              <li><strong>What this means:</strong> This information stays on your device and is never sent to us or anyone else</li>
              <li><strong>Your control:</strong> You can clear this information anytime through your browser settings</li>
            </ul>
            <p className="mt-4 font-bold text-slate-900">Important: This local storage is managed entirely by your browser and device—not by ConnectGo. We have no access to it.</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">4. Third-Party Services and Hosting</h2>
            <p>ConnectGo is hosted on Netlify, a web hosting platform. Here's what you should know:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Hosting infrastructure:</strong> Netlify may collect standard technical information (like IP addresses) to deliver the website to your browser</li>
              <li><strong>Not our collection:</strong> This is standard web hosting practice and not data collection by ConnectGo</li>
              <li><strong>Their policies:</strong> Netlify has its own privacy policy that governs their services</li>
              <li><strong>No integration:</strong> We don't receive or access any information Netlify may collect as part of hosting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">5. Cookies and Tracking Technologies</h2>
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl text-slate-800 mb-4">
              <strong>No cookies are used by ConnectGo.</strong>
            </div>
            <p>We do not use:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Cookies for tracking</li>
              <li>Analytics services (like Google Analytics)</li>
              <li>Advertising networks</li>
              <li>Social media tracking pixels</li>
              <li>Any other tracking or monitoring technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">6. Your Rights and Control</h2>
            <p>Because no data are collected via the web app, there's no personal information for you to access, correct, or delete. You have complete privacy by design:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Play anonymously:</strong> You can use ConnectGo without revealing any information about yourself</li>
              <li><strong>No data requests needed:</strong> There's no data to request or delete</li>
              <li><strong>No consent required:</strong> Since we don't collect data, there's no data collection to consent to</li>
              <li><strong>Browser control:</strong> You can manage any locally stored browser data through your browser settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">7. Children's Privacy</h2>
            <p>ConnectGo is designed with young adults in mind, and we take privacy seriously for users of all ages:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>No data collection:</strong> Because we don't collect any data, there's minimal risk to children's privacy</li>
              <li><strong>COPPA compliant:</strong> Our no-collection approach exceeds requirements of children's privacy laws like COPPA (Children's Online Privacy Protection Act)</li>
              <li><strong>Safe by design:</strong> Parents and educators can be confident that students can use ConnectGo without privacy concerns</li>
              <li><strong>No age verification:</strong> Since we don't collect data, we don't need to verify age or obtain parental consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">8. Data Security</h2>
            <p>Since no data are collected via the web app:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>There's no personal information that could be compromised</li>
              <li>There's no database of user information to secure</li>
              <li>There's no risk of a data breach affecting your privacy</li>
              <li>You can use the game with peace of mind</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">9. International Users</h2>
            <p>ConnectGo can be accessed from anywhere in the world. Regardless of your location:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>No data are collected from users in any country or region</li>
              <li>No cross-border data transfers occur because there's no data to transfer</li>
              <li>We comply with privacy regulations like GDPR, CCPA, and others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">10. External Links</h2>
            <p>ConnectGo may contain links to external websites or resources. Please note:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Those external sites have their own privacy policies</li>
              <li>We're not responsible for their privacy practices</li>
              <li>We encourage you to review their policies before providing any information</li>
              <li>When you leave ConnectGo, this Privacy Policy no longer applies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">11. Changes to This Privacy Policy</h2>
            <p>While our commitment to not collecting data is fundamental to ConnectGo, we may update this Privacy Policy to improve clarity or reflect any changes. If we make changes:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>We'll update the "Last Updated" date at the top of this page</li>
              <li>We'll maintain our core principle of data minimization</li>
              <li>If we ever decide to collect data, we'll clearly notify users and seek appropriate consent</li>
              <li>We encourage periodic review of this policy for peace of mind</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">12. Contact and Questions</h2>
            <p>If you have questions about this Privacy Policy or our privacy practices, we're here to help. Please reach out to: tc@tcchin.com</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">13. Your Consent</h2>
            <p>By using ConnectGo, you acknowledge that you've read and understood this Privacy Policy. More importantly, you can use ConnectGo with confidence knowing that:</p>
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl text-emerald-900 font-bold">
              No data are collected via the web app. Your privacy is protected by design, not just by policy.
            </div>
          </section>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-2xl text-slate-800">
            <strong className="text-blue-900 block mb-2">💡 Privacy by Design:</strong>
            We've developed a game that's fully functional, engaging, and fun, without collecting your personal data. You can enjoy ConnectGo knowing your privacy is fully protected. Play freely, learn joyfully, and connect confidently.
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Privacy;